import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map } from 'rxjs';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { TenantService } from '../services/tenant.service';
import { TenantDetail, tenantAttributes } from '../models/tenant.model';
import { BreadcrumbService } from '../../../../shared/components/breadcrumbs/breadcrumb.service';
import { ToastService } from '../../../../shared/services/toast-service';
import { ModalService } from '../../../../shared/components/modal/modal.service';
import { EditTenantDialogComponent } from '../edit-tenant-dialog/edit-tenant-dialog.component';
import { InviteMemberDialogComponent } from '../invite-member-dialog/invite-member-dialog.component';
import { LabelBadge } from '../../../../shared/components/label-badge/label-badge';

export type TenantDetailTab = 'overview' | 'users';

/**
 * Tenant detail shell (/admin/tenants/:tenantId) — super-admin only.
 * The design's tenant-detail view: name + status header with Edit settings /
 * Disable tenant / Invite user actions, and an Overview | Users tab strip whose
 * content is routed (overview at '', the users grid at 'users'). Owns the
 * tenant fetch; the overview tab reads it via DI instead of refetching.
 */
@Component({
  selector: 'app-admin-tenant-detail',
  standalone: true,
  imports: [RouterOutlet, TranslocoDirective, EditTenantDialogComponent, InviteMemberDialogComponent, LabelBadge],
  templateUrl: './tenant-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminTenantDetailComponent implements OnInit {
  private readonly tenantService = inject(TenantService);
  private readonly breadcrumbs = inject(BreadcrumbService);
  private readonly toastService = inject(ToastService);
  private readonly modal = inject(ModalService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly tenantId = this.route.snapshot.paramMap.get('tenantId') ?? '';

  readonly tenant = signal<TenantDetail | null>(null);
  readonly isEditOpen = signal(false);
  readonly isInviteOpen = signal(false);

  /** Bumped after a successful invite so the routed tab content (users grid /
   *  overview card), which outlives this event, can refetch its member data. */
  readonly memberChange = signal(0);

  /** Which tab the child outlet is showing, derived from the active route. */
  readonly activeTab = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.currentTab()),
    ),
    { initialValue: this.currentTab() },
  );

  ngOnInit(): void {
    this.fetch();
  }

  /** Absolute navigation — relative paths are ambiguous from a ''-path shell. */
  showTab(tab: TenantDetailTab): void {
    const base = ['/admin', 'tenants', this.tenantId];
    void this.router.navigate(tab === 'users' ? [...base, 'users'] : base);
  }

  openEdit(): void {
    this.isEditOpen.set(true);
  }

  closeEdit(): void {
    this.isEditOpen.set(false);
  }

  handleEdited(): void {
    this.closeEdit();
    this.fetch();
  }

  invite(): void {
    this.isInviteOpen.set(true);
  }

  closeInvite(): void {
    this.isInviteOpen.set(false);
  }

  handleInvited(): void {
    this.closeInvite();
    this.memberChange.update((n) => n + 1);
  }

  async toggleEnabled(): Promise<void> {
    const tenant = this.tenant();
    if (!tenant) return;
    if (tenant.enabled) {
      const confirmed = await this.modal.confirm({
        kind: 'destructive',
        destructive: true,
        title: this.transloco.translate('admin.tenants.detailPage.disable.title'),
        message: this.transloco.translate('admin.tenants.detailPage.disable.message', { name: tenant.name }),
        confirmLabel: this.transloco.translate('admin.tenants.detailPage.disable.confirm'),
      });
      if (!confirmed) return;
    }
    this.setEnabled(tenant, !tenant.enabled);
  }

  private setEnabled(tenant: TenantDetail, enabled: boolean): void {
    // PUT takes a full replacement body (same contract the edit dialog uses).
    // Contact/region live in attributes and must be sent back or they're wiped.
    this.tenantService
      .updateTenant(tenant.id, {
        name: tenant.name,
        alias: tenant.alias,
        enabled,
        description: tenant.description,
        domains: tenant.domains ? tenant.domains.map((d) => ({ name: d.name })) : null,
        attributes: tenantAttributes(tenant.contact, tenant.region),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.transloco.translate('admin.tenants.toast.updated'), 'success');
          this.fetch();
        },
        error: () => this.toastService.show(this.transloco.translate('admin.tenants.toast.updateFailed'), 'error'),
      });
  }

  private fetch(): void {
    // Guard: an empty id would collapse GET /organizations/{id} into the list URL.
    if (!this.tenantId) return;
    this.tenantService
      .getTenant(this.tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tenant) => {
          this.tenant.set(tenant);
          // Covers hard refresh / direct links, where the list never named it.
          this.breadcrumbs.setLabel('tenantId', tenant.id, tenant.name);
        },
        error: () => this.toastService.show(this.transloco.translate('admin.tenants.toast.loadOneFailed'), 'error'),
      });
  }

  private currentTab(): TenantDetailTab {
    return this.route.snapshot.firstChild?.routeConfig?.path === 'users' ? 'users' : 'overview';
  }
}
